import { useEffect, useState } from 'react';

/**
 * usePwaInstall — captures the beforeinstallprompt event so we can fire the
 * native browser install prompt on demand. Falls back to platform-specific
 * instructions when the prompt isn't available (iOS Safari, already installed, etc.).
 */
export default function usePwaInstall() {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Detect if already running as installed app
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };
    const onAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPromptEvent) return { outcome: 'unavailable' };
    try {
      installPromptEvent.prompt();
      const result = await installPromptEvent.userChoice;
      if (result?.outcome === 'accepted') {
        setIsInstalled(true);
        setInstallPromptEvent(null);
      }
      return result;
    } catch {
      return { outcome: 'error' };
    }
  };

  // Detect platform for fallback instructions
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);

  return {
    canInstall: !!installPromptEvent && !isStandalone && !isInstalled,
    isStandalone,
    isInstalled,
    isIos,
    isSafari,
    promptInstall,
  };
}
